// Product analytics for the web's view-only hazards, in the apps' vocabulary
// (snake_case, same event names and properties as iOS/Android) so one PostHog
// question covers every platform:
//
//   hazards_toggled        { enabled }                                  — Maps sheet → More → Hazards switch
//   hazard_viewed          { hazard_type, confirmation_state, is_mine,
//                            screen, platform: 'web' }                  — details panel opened
//   hazard_app_cta_clicked { hazard_type }                              — "Open in app" tapped
//
// `screen` reuses the On This Map screen names the overlay events already send
// from the web (overlayScreenForPath). Sent through `window.mixpanel`, the
// facade that routes to PostHog.

import { overlayScreenForPath, OverlayScreen } from '../overlayToggleAnalytics';
import { ConfirmationState, HazardType } from './hazardTypes';

export const HAZARDS_TOGGLED_EVENT = 'hazards_toggled';
export const HAZARD_VIEWED_EVENT = 'hazard_viewed';
export const HAZARD_APP_CTA_CLICKED_EVENT = 'hazard_app_cta_clicked';

export interface HazardAnalyticsEvent {
  event: string;
  props: Record<string, unknown>;
}

export function hazardsToggledEvent(enabled: boolean): HazardAnalyticsEvent {
  return { event: HAZARDS_TOGGLED_EVENT, props: { enabled: !!enabled } };
}

export interface ViewedHazard {
  hazardType: HazardType;
  confirmationState: ConfirmationState;
  isMine: boolean;
}

export function hazardViewedEvent(hazard: ViewedHazard, screen: OverlayScreen | string): HazardAnalyticsEvent {
  return {
    event: HAZARD_VIEWED_EVENT,
    props: {
      hazard_type: hazard.hazardType,
      confirmation_state: hazard.confirmationState,
      is_mine: !!hazard.isMine,
      screen,
      platform: 'web',
    },
  };
}

export function hazardAppCtaClickedEvent(hazardType: HazardType): HazardAnalyticsEvent {
  return { event: HAZARD_APP_CTA_CLICKED_EVENT, props: { hazard_type: hazardType } };
}

/** The current page's screen name, in the shared vocabulary. */
export function hazardScreen(pathname: string = typeof window === 'undefined' ? '/' : window.location.pathname): OverlayScreen {
  return overlayScreenForPath(pathname);
}

/** Fire-and-forget; a missing analytics shim must never break the map. */
export function trackHazardEvent({ event, props }: HazardAnalyticsEvent): void {
  try {
    (window as any).mixpanel?.track(event, props);
  } catch {
    // Telemetry must never break the flow it is reporting on.
  }
}
